import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, getWeek, getYear } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  ShoppingCart, 
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Euro,
  CheckCircle,
  Clock,
  Route as RouteIcon,
  FileText,
  User,
  Car,
  Target,
  ChevronLeft,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

const COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4'];

export default function ReportsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('weekly');
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Weekly reports state
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(getWeek(subDays(new Date(), 7)));
  const [selectedYear, setSelectedYear] = useState(getYear(subDays(new Date(), 7)));
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [generatingReports, setGeneratingReports] = useState(false);
  const [selectedUserReport, setSelectedUserReport] = useState(null);
  
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'weekly') {
      loadWeeklyReports();
    }
  }, [selectedWeek, selectedYear, activeTab]);

  const loadInitialData = async () => {
    try {
      const [teamRes, weeksRes] = await Promise.all([
        isAdmin ? api().get('/team') : Promise.resolve({ data: [] }),
        isAdmin ? api().get('/reports/weekly/available-weeks') : Promise.resolve({ data: [] })
      ]);
      setTeamMembers(teamRes.data || []);
      setAvailableWeeks(weeksRes.data || []);
    } catch (error) {
      console.error('Error loading initial data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyReports = async () => {
    try {
      const response = await api().get(`/reports/weekly?week=${selectedWeek}&year=${selectedYear}`);
      setWeeklyReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading weekly reports', error);
    }
  };

  const generateWeeklyReports = async () => {
    setGeneratingReports(true);
    try {
      await api().post(`/reports/generate-weekly?week=${selectedWeek}&year=${selectedYear}`);
      toast.success('Relatórios semanais gerados com sucesso!');
      loadWeeklyReports();
      loadInitialData(); // Refresh available weeks
    } catch (error) {
      toast.error('Erro ao gerar relatórios');
    } finally {
      setGeneratingReports(false);
    }
  };

  const changeWeek = (direction) => {
    let newWeek = selectedWeek + direction;
    let newYear = selectedYear;
    
    if (newWeek > 52) {
      newWeek = 1;
      newYear++;
    } else if (newWeek < 1) {
      newWeek = 52;
      newYear--;
    }
    
    setSelectedWeek(newWeek);
    setSelectedYear(newYear);
  };

  const exportUserReport = (report) => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += `Relatório Semanal - ${report.user_name}\n`;
    csvContent += `Semana ${report.week_number}/${report.year} (${report.start_date} a ${report.end_date})\n\n`;
    csvContent += `Métrica,Valor\n`;
    csvContent += `Total Vendas,${report.total_sales}€\n`;
    csvContent += `Encomendas,${report.total_orders}\n`;
    csvContent += `Visitas,${report.total_visits}\n`;
    csvContent += `Rotas,${report.total_routes}\n`;
    csvContent += `Clientes Visitados,${report.total_clients_visited}\n`;
    csvContent += `Km Percorridos,${report.total_km}\n`;
    csvContent += `Taxa de Conclusão,${report.visit_completion_rate}%\n\n`;
    
    csvContent += `\nResumo Diário\n`;
    csvContent += `Data,Visitas,Vendas,Rotas\n`;
    report.daily_breakdown?.forEach(d => {
      csvContent += `${d.date},${d.visits},${d.sales}€,${d.routes}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `relatorio_${report.user_name}_semana${report.week_number}_${report.year}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Relatório exportado!');
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'gestor': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'gestor': return 'Gestor';
      default: return 'Vendedor';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-amber-500" />
            Relatórios
          </h1>
          <p className="text-muted-foreground">Análise de performance por utilizador</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="weekly" className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            Relatórios Semanais
          </TabsTrigger>
          <TabsTrigger value="compare" className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            Comparativo
          </TabsTrigger>
        </TabsList>

        {/* Weekly Reports Tab */}
        <TabsContent value="weekly" className="space-y-6">
          {/* Week Selector */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => changeWeek(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="text-center min-w-[200px]">
                    <p className="font-bold text-lg">Semana {selectedWeek}</p>
                    <p className="text-sm text-muted-foreground">{selectedYear}</p>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => changeWeek(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                
                {isAdmin && (
                  <Button 
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                    onClick={generateWeeklyReports}
                    disabled={generatingReports}
                  >
                    {generatingReports ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Gerar Relatórios
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reports by User */}
          {weeklyReports.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Sem relatórios para esta semana</p>
                <p className="text-muted-foreground">
                  {isAdmin ? 'Clique em "Gerar Relatórios" para criar os relatórios semanais' : 'Contacte o administrador para gerar os relatórios'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {weeklyReports.map((report) => (
                <Card 
                  key={report.user_id} 
                  className={`cursor-pointer transition-all hover:shadow-lg ${selectedUserReport?.user_id === report.user_id ? 'ring-2 ring-amber-500' : ''}`}
                  onClick={() => setSelectedUserReport(report)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{report.user_name}</CardTitle>
                          <Badge className={`text-xs ${getRoleColor(report.user_role)}`}>
                            {getRoleName(report.user_role)}
                          </Badge>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); exportUserReport(report); }}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Euro className="h-4 w-4 text-amber-500" />
                        <div>
                          <p className="text-muted-foreground">Vendas</p>
                          <p className="font-bold">{report.total_sales?.toLocaleString()}€</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        <div>
                          <p className="text-muted-foreground">Visitas</p>
                          <p className="font-bold">{report.total_visits}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <RouteIcon className="h-4 w-4 text-blue-500" />
                        <div>
                          <p className="text-muted-foreground">Rotas</p>
                          <p className="font-bold">{report.total_routes}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Car className="h-4 w-4 text-purple-500" />
                        <div>
                          <p className="text-muted-foreground">Km</p>
                          <p className="font-bold">{report.total_km} km</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress bar for completion rate */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Taxa de conclusão</span>
                        <span className="font-medium">{report.visit_completion_rate}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            report.visit_completion_rate >= 80 ? 'bg-emerald-500' :
                            report.visit_completion_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${report.visit_completion_rate}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Detailed View */}
          {selectedUserReport && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-[Manrope]">
                      Detalhes: {selectedUserReport.user_name}
                    </CardTitle>
                    <CardDescription>
                      Semana {selectedUserReport.week_number}/{selectedUserReport.year} 
                      ({selectedUserReport.start_date} a {selectedUserReport.end_date})
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => setSelectedUserReport(null)}>
                    Fechar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-700">Total Vendas</p>
                    <p className="text-xl font-bold text-amber-900">{selectedUserReport.total_sales?.toLocaleString()}€</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-700">Encomendas</p>
                    <p className="text-xl font-bold text-blue-900">{selectedUserReport.total_orders}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs text-emerald-700">Visitas</p>
                    <p className="text-xl font-bold text-emerald-900">{selectedUserReport.total_visits}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-700">Clientes</p>
                    <p className="text-xl font-bold text-purple-900">{selectedUserReport.total_clients_visited}</p>
                  </div>
                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <p className="text-xs text-indigo-700">Rotas</p>
                    <p className="text-xl font-bold text-indigo-900">{selectedUserReport.total_routes}</p>
                  </div>
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-700">Km Percorridos</p>
                    <p className="text-xl font-bold text-slate-900">{selectedUserReport.total_km} km</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Daily Breakdown Chart */}
                  <div>
                    <h4 className="font-medium mb-3">Atividade Diária</h4>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedUserReport.daily_breakdown || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={(d) => d.split('-')[2]} />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="visits" fill="#10b981" name="Visitas" />
                          <Bar yAxisId="right" dataKey="sales" fill="#f59e0b" name="Vendas (€)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Top Clients */}
                  <div>
                    <h4 className="font-medium mb-3">Melhores Clientes</h4>
                    {selectedUserReport.top_clients?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedUserReport.top_clients.map((client, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 flex items-center justify-center bg-amber-500 text-white text-xs font-bold rounded-full">
                                {idx + 1}
                              </span>
                              <span className="font-medium">{client.name}</span>
                            </div>
                            <span className="text-amber-600 font-bold">{client.sales?.toLocaleString()}€</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">Sem dados de clientes</p>
                    )}
                  </div>
                </div>

                {/* Daily Breakdown Table */}
                <div>
                  <h4 className="font-medium mb-3">Resumo por Dia</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Visitas</TableHead>
                        <TableHead>Vendas</TableHead>
                        <TableHead>Rotas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedUserReport.daily_breakdown?.map((day, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            {format(new Date(day.date), "EEEE, d 'de' MMMM", { locale: pt })}
                          </TableCell>
                          <TableCell>{day.visits}</TableCell>
                          <TableCell>{day.sales?.toLocaleString()}€</TableCell>
                          <TableCell>{day.routes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Compare Tab */}
        <TabsContent value="compare" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Comparativo da Equipa - Semana {selectedWeek}/{selectedYear}</CardTitle>
            </CardHeader>
            <CardContent>
              {weeklyReports.length > 0 ? (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={weeklyReports.map(r => ({
                        name: r.user_name?.split(' ')[0] || 'N/A',
                        vendas: r.total_sales || 0,
                        visitas: r.total_visits || 0,
                        km: r.total_km || 0
                      }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="vendas" fill="#f59e0b" name="Vendas (€)" />
                      <Bar dataKey="visitas" fill="#10b981" name="Visitas" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-12">
                  Gere os relatórios semanais para ver o comparativo
                </p>
              )}
            </CardContent>
          </Card>

          {/* Ranking Table */}
          {weeklyReports.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Ranking da Semana</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pos.</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Vendas</TableHead>
                      <TableHead>Visitas</TableHead>
                      <TableHead>Km</TableHead>
                      <TableHead>Taxa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {weeklyReports
                      .sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0))
                      .map((report, idx) => (
                        <TableRow key={report.user_id}>
                          <TableCell>
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                              idx === 0 ? 'bg-amber-500 text-white' :
                              idx === 1 ? 'bg-slate-400 text-white' :
                              idx === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100'
                            }`}>
                              {idx + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{report.user_name}</TableCell>
                          <TableCell>
                            <Badge className={getRoleColor(report.user_role)}>
                              {getRoleName(report.user_role)}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-bold text-amber-600">{report.total_sales?.toLocaleString()}€</TableCell>
                          <TableCell>{report.total_visits}</TableCell>
                          <TableCell>{report.total_km} km</TableCell>
                          <TableCell>
                            <Badge className={
                              report.visit_completion_rate >= 80 ? 'bg-emerald-100 text-emerald-800' :
                              report.visit_completion_rate >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                            }>
                              {report.visit_completion_rate}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
